import React, { useState, useEffect, useRef } from 'react';
import { Zap, MapPin, Grid3x3, Leaf } from 'lucide-react';

const StatCard = ({ icon: Icon, value, label, suffix = '', duration = 2000 }) => {
  const [count, setCount] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);
  const elementRef = useRef(null);

  useEffect(() => {
    const currentElement = elementRef.current;
    
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !hasAnimated) {
          setHasAnimated(true);
          
          // Handle numeric values
          const numericValue = parseInt(value.toString().replace(/[^0-9]/g, ''));
          if (isNaN(numericValue) || numericValue === 0) {
            setCount(value);
            return;
          }
          
          let start = 0;
          const end = numericValue;
          const incrementTime = Math.max(duration / end, 20);
          
          const timer = setInterval(() => {
            start += 1;
            setCount(start);
            if (start >= end) {
              clearInterval(timer);
              setCount(end);
            }
          }, incrementTime);
          
          return () => clearInterval(timer);
        }
      },
      { threshold: 0.5 }
    );

    if (currentElement) {
      observer.observe(currentElement);
    }

    return () => {
      if (currentElement) {
        observer.unobserve(currentElement);
      }
    };
  }, [value, hasAnimated, duration]);

  return (
    <div 
      ref={elementRef}
      className="flex flex-col items-center p-8 bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-all duration-300 hover:scale-105"
    >
      <div className="w-16 h-16 bg-emerald-50 rounded-full flex items-center justify-center mb-4">
        <Icon className="w-8 h-8 text-emerald-700" />
      </div>
      <div className="text-4xl font-bold text-gray-900 mb-2">
        {count}{suffix}
      </div>
      <div className="text-sm text-gray-600 text-center font-medium">
        {label}
      </div>
    </div>
  );
};

const Stats = () => {
  const stats = [
    {
      icon: Zap,
      value: 10,
      suffix: ' MW',
      label: 'Installed Capacity'
    },
    {
      icon: MapPin,
      value: 1,
      suffix: '',
      label: 'Madhya Pradesh Project'
    },
    {
      icon: Grid3x3,
      value: 100,
      suffix: '%',
      label: 'Grid-Connected Utility Power'
    },
    {
      icon: Leaf,
      value: 12,
      suffix: 'K+',
      label: 'Tonnes CO₂ Reduced Annually'
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-b from-white to-gray-50">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {stats.map((stat, index) => (
            <StatCard key={index} {...stat} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Stats;
